import * as THREE from "three";
import { BaseManager } from "../base/BaseManager";
import { Context } from "../core/context";
import { DbLayer } from "../core/DbLayer";
import { Block, BlockReference, Line, Mesh } from "../vendor/protobuf/packet2d";
import { MLayer } from "../core/MLayer";
import { DBEntity } from "../core/DBEntity";
import {
  AddLineSegmentsIndexs,
  FreeThreeObject,
  GetInsertLines,
  GetInsertMeshs,
} from "../utils/GeometryUtils";
import { DbLine } from "../core/DbLine";
import { DbMesh } from "../core/DbMesh";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils";
import { IndserObject } from "../core/IndserObject";
import { DbBlockRecord } from "../core/DbBlockRecord";
import { DbBlockInstance } from "../core/DbBlockInstance";
import { generateUUID } from "three/src/math/MathUtils";
import { InsertDatas } from "@/core/Parameters";
import { FMathUtils } from "@/CadEngine";
import {
  computeBoundsTree,
  disposeBoundsTree,
  acceleratedRaycast,
} from "three-mesh-bvh";

/**
 * 模型管理
 */
export class ModelManager extends BaseManager {
  /**
   * 线集
   */
  private m_lineGeos: {
    [key: string]: {
      lines: DbLine[];
      model: THREE.LineSegments | THREE.Line;
      canEdit: boolean;
    };
  } = {};
  /***
   * mesh集
   */
  private m_meshGeos: {
    [key: string]: { meshs: DbMesh[]; canEdit: boolean; model: THREE.Mesh };
  } = {};

  private m_references: {
    [key: string]: {
      key: string[];
      lines: DbLine[];
      mesh: DbMesh[];
      lineGeo: THREE.BufferGeometry | undefined;
      meshGeo: THREE.BufferGeometry | undefined;
    };
  } = {};
  /***
   * 块表，所谓类型存储， key为id，对应到m_instances的blockRecord
   */
  private m_blocks: { [key: string]: DbBlockRecord } = {};
  /**
   * 块表在场景中对应的实例
   */
  // private m_instances: { [key: string]: DbBlockInstance[] } = {};
  private m_instances: Map<string, DbBlockInstance> = new Map();

  private m_lineGeoCount: number = 0;
  private m_meshGeoCount: number = 0;

  public getInstancesMatrix(rootId: string) {
    if (this.m_instances.has(rootId)) {
      return this.m_instances.get(rootId);
    }
  }
  /**
   * 模型挂载根节点
   */
  private m_model: MLayer;
  private m_layers: { [key: string]: DbLayer };

  public isInsert: boolean = false;
  private m_insertGroup: THREE.Group = new THREE.Group();
  public get InsertGroup() {
    return this.m_insertGroup;
  }
  private m_insertDatas: InsertDatas = { lines: {}, meshs: {} };

  public get InsertDatas() {
    return this.m_insertDatas;
  }
  public set InsertDatas(value: InsertDatas) {
    this.m_insertDatas = value;
  }
  private m_insertBlocks: Map<string, DbBlockRecord> = new Map();
  public set InsertBlocks(value: Map<string, DbBlockRecord>) {
    this.m_insertBlocks = value;
  }

  public get Group() {
    return this.m_references;
  }
  public SetReferences(dbEntity: DbLine | DbMesh) {
    let { root_reference_id } = dbEntity;
    let key = this.m_context.ToolsManager.getKey(dbEntity);
    if (root_reference_id) {
      if (!this.m_references[root_reference_id]) {
        this.m_references[root_reference_id] = {
          key: [key],
          lines: [],
          mesh: [],
          lineGeo: undefined,
          meshGeo: undefined,
        };
      }
      if (!this.m_references[root_reference_id].key.includes(key)) {
        this.m_references[root_reference_id].key.push(key);
      }
      if (dbEntity instanceof DbLine) {
        this.m_references[root_reference_id].lines.push(dbEntity);
      } else if (dbEntity instanceof DbMesh) {
        this.m_references[root_reference_id].mesh.push(dbEntity);
      }
    }
  }
  public updateReferencesGeometry(dbEntitys: (DbLine | DbMesh)[]) {
    let rootIds: string[] = [];
    dbEntitys.forEach((item) => {
      if (item?.root_reference_id) {
        if (!rootIds.includes(item.root_reference_id)) {
          rootIds.push(item.root_reference_id);
        }
      }
    });
    for (let i = 0; i < rootIds.length; i++) {
      let rootId = rootIds[i];
      if (this.m_references[rootId]) {
        let { lines, mesh } = this.m_references[rootId];
        this.m_references[rootId].lineGeo = this.updatedGeometry(lines);
        this.m_references[rootId].meshGeo = this.updatedGeometry(mesh);
      }
    }
  }
  private updatedGeometry(datas: DbLine[] | DbMesh[]) {
    let index = 0;
    let indexes: number[] = [];
    let attributeSum: THREE.BufferAttribute[] = [];
    let geo = new THREE.BufferGeometry();
    datas.forEach((item) => {
      if (item instanceof DbLine) {
        item.indexes?.forEach((v) => {
          indexes.push(v + index);
        });
        index += item.points.length / 3;
        let attribute = new THREE.BufferAttribute(
          new Float32Array(item.points),
          3
        );
        attributeSum.push(attribute);
      } else if (item instanceof DbMesh) {
        let attribute = new THREE.BufferAttribute(
          new Float32Array(item.vertexs),
          3
        );
        attributeSum.push(attribute);
      }
    });
    if (attributeSum.length) {
      let mergeAttribute =
        BufferGeometryUtils.mergeBufferAttributes(attributeSum);

      geo.setAttribute(
        "position",
        new THREE.BufferAttribute(mergeAttribute.array, 3)
      );
      if (indexes.length) {
        geo.setIndex(indexes);
      }
      return geo;
    }
    return undefined;
  }
  public AddBlockInstance(dbEntity: DbLine | DbMesh, matrix: THREE.Matrix4) {
    let { root_reference_id, blockId, extend_id, layerId } = dbEntity;
    let block = this.m_blocks[blockId];
    if (block && root_reference_id) {
      if (block.instances.length) {
        for (let i = 0; i < block.instances.length; i++) {
          if (!this.m_instances.has(root_reference_id)) {
            let instances = this.m_insertBlocks.get(blockId)?.instances;
            if (instances && instances.length) {
              let ins = instances[0];
              let blockInstance = new DbBlockInstance({
                id: root_reference_id,
                blockId: ins.blockId,
                layerId: ins.layerId,
                matrix,
                blockRecord: block,
                extend_id,
              });
              this.m_instances.set(root_reference_id, blockInstance);
              block.instances.push(blockInstance);
            }
          }
        }
      } else {
        let instances = this.m_insertBlocks.get(blockId)?.instances;
        if (instances && instances.length) {
          let ins = instances[0];
          let blockInstance = new DbBlockInstance({
            id: root_reference_id,
            blockId: ins.blockId,
            layerId: ins.layerId,
            matrix,
            blockRecord: block,
            extend_id,
          });
          block.instances.push(blockInstance);
          if (!this.m_instances.has(root_reference_id)) {
            this.m_instances.set(root_reference_id, blockInstance);
          }
        }
      }
    }
  }

  constructor(context: Context) {
    super(context);
    this.m_layers = {};
    this.m_model = this.m_context.SceneManager.ModelLayer;
    (THREE.BufferGeometry.prototype as any).computeBoundsTree =
      computeBoundsTree;
    (THREE.BufferGeometry.prototype as any).disposeBoundsTree =
      disposeBoundsTree;
    THREE.Mesh.prototype.raycast = acceleratedRaycast; // 替换默认射线检测方法
    this.addInsertGroup();
  }
  public addInsertGroup() {
    this.m_insertGroup = new THREE.Group();
    this.m_insertGroup.name = "insertGroup";
    this.m_insertGroup.visible = false;
    this.m_model.add(this.m_insertGroup);
  }

  public traverse(callback: (object: THREE.Object3D) => any) {
    this.m_model.traverse(callback);
    document.addEventListener;
  }

  public getLayerIdsByNames(...layerNames: string[]): string[] {
    let ids = [];
    for (let key in this.m_context.ModelManager.LayerTable) {
      let item = this.m_context.ModelManager.LayerTable[key];
      for (let layerName of layerNames) {
        if (item.Name.toLowerCase().includes(layerName.toLowerCase()))
          ids.push(key);
      }
    }
    return ids;
  }

  public getLayersByNames(...layerNames: string[]): DbLayer[] {
    let layers = [];
    for (let key in this.m_context.ModelManager.LayerTable) {
      let item = this.m_context.ModelManager.LayerTable[key];
      for (let layerName of layerNames) {
        if (item.Name.toLowerCase().includes(layerName.toLowerCase()))
          layers.push(item);
      }
    }
    return layers;
  }

  public GetLayerByNameColor(layerName: string, color: number) {
    for (let key in this.m_context.ModelManager.LayerTable) {
      let item = this.m_context.ModelManager.LayerTable[key];
      if (item.Name.toLowerCase() == layerName.toLowerCase()) {
        if (!item.ObjectColors.includes(color)) {
          item.ObjectColors.push(color);
        }
        return item;
      }
    }
    return undefined;
  }

  public GetLayerByName(layerName: string) {
    for (let key in this.m_context.ModelManager.LayerTable) {
      let item = this.m_context.ModelManager.LayerTable[key];
      if (item.Name.toLowerCase() == layerName.toLowerCase()) return item;
    }
    return undefined;
  }

  public GetLayerById(Id: string) {
    for (let key in this.m_context.ModelManager.LayerTable) {
      let item = this.m_context.ModelManager.LayerTable[key];
      if (item.Id == Id) return item;
    }
    return undefined;
  }

  public GetLayerByIdColor(Id: string, color: number) {
    for (let key in this.m_context.ModelManager.LayerTable) {
      let item = this.m_context.ModelManager.LayerTable[key];
      if (item.Id == Id) {
        if (!item.ObjectColors.includes(color)) {
          item.ObjectColors.push(color);
        }
        return item;
      }
    }
    return undefined;
  }

  public get LayerTable() {
    return this.m_layers;
  }
  public get Layers() {
    let layers = [];
    for (const key in this.m_layers) {
      layers.push(this.m_layers[key]);
    }
    return layers;
  }
  public get Lines() {
    return this.m_lineGeos;
  }

  public get Meshs() {
    return this.m_meshGeos;
  }

  public get Model() {
    return this.m_model;
  }

  public get Blocks() {
    return this.m_blocks;
  }

  // 判断字符串是否含有乱码
  // private isGarbledCode(str: string) {
  //   if (!str) return;
  //   let isGarbled = false;
  //   for (let index = 0; index < str.length; index++) {
  //     const charCode = str.charCodeAt(index);
  //     if (charCode > 256) {
  //       isGarbled = true;
  //       break;
  //     }
  //   }
  //   if (!isGarbled) return str;
  //   let result = "";
  //   for (let index = 0; index < str.length; index++) {
  //     const charCode = str.charCodeAt(index);
  //     result += charCode.toString(16);
  //   }
  //   return result;
  // }
  private m_maxZ = 0;
  private m_minZ = 0;
  private m_sampleDatas: any[] = [];
  public get MaxZ() {
    return this.m_maxZ;
  }
  public set MaxZ(value: number) {
    this.m_maxZ = value;
  }
  public get MinZ() {
    return this.m_minZ;
  }
  public set MinZ(value: number) {
    this.m_minZ = value;
  }
  public get SampleDatas() {
    return this.m_sampleDatas;
  }
  public set SampleDatas(sampleDatas) {
    this.m_sampleDatas = sampleDatas;
  }

  public GetSampleDataByLayerName(LayerName: string) {
    for (let i = 0; i < this.SampleDatas.length; i++) {
      let item = this.SampleDatas[i];
      if (item.LayerName == LayerName) return item;
    }
    return undefined;
  }

  addlines(
    lines: { [key: string]: DbLine[] },
    canEdit: boolean,
    isSourceData: boolean | undefined = undefined
  ) {
    let addLines: DbLine[] = [];
    for (const key in lines) {
      let items = lines[key];
      let array: DbLine[] = [];
      if (items) {
        let ps = key.split("_");
        items.forEach((item) => {
          // let str = item.root_block_id!;
          // item.root_block_id=  this.isGarbledCode(str)
          let line = DbLine.clone(item);
          if (isSourceData) {
            line.isSourceData = isSourceData;
          }
          line.color = parseInt(ps[1]);
          if (line.root_reference_id && !this.isInsert) {
            if (!this.m_references[line.root_reference_id]) {
              this.m_references[line.root_reference_id] = {
                key: [key],
                lines: [],
                mesh: [],
                lineGeo: undefined,
                meshGeo: undefined,
              };
            }

            if (!this.m_references[line.root_reference_id].key.includes(key)) {
              this.m_references[line.root_reference_id].key.push(key);
            }
            this.m_references[line.root_reference_id].lines.push(line);
          }
          addLines.push(line);
          array.push(line);
        });
        let model = this.addLinesToModel(ps[0], parseInt(ps[1]), array);
        this.updateReferencesGeometry(items);
        if (this.isInsert) {
          if (this.m_insertDatas.lines[key]) {
            this.m_insertDatas.lines[key].lines.push(...array);
          } else {
            this.m_insertDatas.lines[key] = {
              lines: array,
              model: model,
              canEdit: canEdit,
            };
          }
          this.m_insertDatas.lines[key].model = model;
        } else {
          if (this.m_lineGeos[key]) {
            this.m_lineGeos[key].lines.push(...array);
          } else {
            this.m_lineGeos[key] = {
              lines: array,
              model: model,
              canEdit: canEdit,
            };
          }
          this.m_lineGeos[key].model = model;
        }
      }
    }

    this.m_context.ViewManager.Render();
    return addLines;
  }

  addMeshs(
    meshs: { [key: string]: DbMesh[] },
    canEdit: boolean,
    isSourceData: boolean | undefined = undefined
  ) {
    let addMeshs: DbMesh[] = [];
    for (const key in meshs) {
      let items = meshs[key];
      let array: DbMesh[] = [];
      let ps = key.split("_");
      items.forEach((item) => {
        // let str = item.root_block_id!;
        // item.root_block_id=  this.isGarbledCode(str)
        let mesh = DbMesh.clone(item);
        if (isSourceData) {
          mesh.isSourceData = isSourceData;
        }
        mesh.color = parseInt(ps[1]);

        if (mesh.root_reference_id && !this.isInsert) {
          if (!this.m_references[mesh.root_reference_id]) {
            this.m_references[mesh.root_reference_id] = {
              key: [key],
              lines: [],
              mesh: [],
              meshGeo: undefined,
              lineGeo: undefined,
            };
          }
          if (!this.m_references[mesh.root_reference_id].key.includes(key)) {
            this.m_references[mesh.root_reference_id].key.push(key);
          }

          this.m_references[mesh.root_reference_id].mesh.push(mesh);
        }
        array.push(mesh);
        addMeshs.push(mesh);
      });
      let model = this.addMeshToModel(ps[0], parseInt(ps[1]), array);
      this.updateReferencesGeometry(items);

      if (this.isInsert) {
        if (this.m_insertDatas.meshs[key]) {
          this.m_insertDatas.meshs[key].meshs.push(...array);
        } else {
          this.m_insertDatas.meshs[key] = {
            meshs: array,
            model: model,
            canEdit: canEdit,
          };
        }
        this.m_insertDatas.meshs[key].model = model;
      } else {
        if (this.m_meshGeos[key]) {
          this.m_meshGeos[key].meshs.push(...array);
        } else {
          this.m_meshGeos[key] = {
            meshs: array,
            canEdit: canEdit,
            model: model,
          };
        }
        this.m_meshGeos[key].model = model;

        delete meshs[key];
      }
    }
    this.m_context.ViewManager.Render();
    return addMeshs;
  }

  addLayers(layers: DbLayer[]) {
    layers.forEach((item) => {
      let temp = this.m_layers[item.Id];
      if (!temp) this.m_layers[item.Id] = item;
    });
  }

  addInstances(instances: { [key: string]: DbBlockInstance[] }) {
    if (this.isInsert) return;
    for (const key in instances) {
      let instance = instances[key];
      for (let i = 0; i < instance.length; i++) {
        if (!this.m_instances.has(instance[i].id)) {
          instance[i].matrix = new THREE.Matrix4().fromArray(
            instance[i].matrix.elements
          );
          this.m_instances.set(instance[i].id, instance[i]);
        }
      }
    }
  }

  setInstances(rootId: string, matrix: THREE.Matrix4) {
    if (this.m_instances.has(rootId)) {
      let instance = this.m_instances.get(rootId);
      instance?.matrix.copy(matrix);
    } else {
      return undefined;
    }
  }

  addBlocks(blocks: { [key: string]: DbBlockRecord }) {
    for (const key in blocks) {
      let items = blocks[key];
      if (!this.m_blocks[key]) {
        this.m_blocks[key] = items;
        if (this.isInsert) {
          let blockRecord = new DbBlockRecord();
          blockRecord.instances = [];
          blockRecord.Name = items.Name;
          blockRecord.Id = items.Id;
          blockRecord.lines = items.lines ?? {};
          blockRecord.meshs = items.meshs ?? {};
          this.m_blocks[key] = blockRecord;
          this.m_insertBlocks.set(key, items);
        }
      } else {
        if (!this.isInsert) {
          this.m_blocks[key].instances = this.m_blocks[key].instances.concat(
            items.instances
          );
        } else {
          this.m_insertBlocks.set(key, items);
        }
      }
      this.bindingExtendId(items);
    }
  }

  bindingExtendId(blockData: DbBlockRecord) {
    let { instances } = blockData;
    if (instances && instances.length) {
      for (let i = 0; i < instances.length; i++) {
        let instance = instances[i];
        let { layerId, id, extend_id } = instance;
        let datas = this.GetRootReferenceIdDatas(id);
        for (let i = 0; i < datas.length; i++) {
          const data = datas[i];
          data.extend_id = extend_id;
        }
        // for (const key in this.Lines) {
        //   let LinesLayerId = key.split("_")[0];
        //   let { lines } = this.Lines[key];
        //   if (LinesLayerId == layerId) {
        //     for (let j = 0; j < lines.length; j++) {
        //       if (lines[j].root_reference_id == id) {
        //         lines[j].extend_id = extend_id;
        //       }
        //     }
        //   }
        // }
        // for (const key in this.Meshs) {
        //   let MeshsLayerId = key.split("_")[0];
        //   let { meshs } = this.Meshs[key];
        //   if (MeshsLayerId == layerId) {
        //     for (let j = 0; j < meshs.length; j++) {
        //       if (meshs[j].root_reference_id == id) {
        //         meshs[j].extend_id = extend_id;
        //       }
        //     }
        //   }
        // }
      }
    }
  }

  addLayer(layer: DbLayer) {
    this.m_layers[layer.Id] = layer;
    return layer;
  }

  addMeshToModel(layerId: string, color: number, meshs: DbMesh[]) {
    let material = this.m_context.MaterialManager.createMeshBasicMaterial(
      new THREE.Color(color)
    );
    material.side = THREE.DoubleSide;
    let posistion: number[] = [];
    meshs.forEach((geo) => {
      geo.vertexs?.forEach((v, i) => {
        if (i % 3 == 2) {
          if (v > this.m_maxZ) {
            this.m_maxZ = v;
          }
          if (v < this.m_minZ) {
            this.m_minZ = v;
          }
        }
        posistion.push(v);
      });
      //关联相关同id的模型
      // let lines = this.m_lineGeos[key]
      // if (lines) {
      //     lines.forEach(line => {
      //         if (geo.id == line.id) {
      //             if (geo.unions == undefined) geo.unions = []
      //             geo.unions?.push(line)
      //             if (line.unions == undefined) line.unions = []
      //             line.unions?.push(geo)
      //         }
      //     })
      // }
    });
    let geometry = new THREE.BufferGeometry();
    let vertexs = new Float32Array(posistion);
    geometry.setAttribute("position", new THREE.BufferAttribute(vertexs, 3));
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();
    let mesh = new THREE.Mesh(geometry, material);
    mesh.userData.layerId = layerId;
    mesh.userData.originalMaterial = mesh.material;
    mesh.renderOrder = -1;
    if (this.m_layers[layerId]) {
      if (!this.isInsert) {
        this.m_layers[layerId].Objects.push(mesh);
        this.m_layers[layerId].MeshCount =
          1 + (this.m_layers[layerId].MeshCount ?? 0);
        if (!this.m_layers[layerId].ObjectColors.includes(color)) {
          this.m_layers[layerId].ObjectColors.push(color);
        }
      }
    }
    this.isInsert ? this.m_insertGroup.add(mesh) : this.m_model.add(mesh);
    return mesh;
  }

  getBlock(root_block_id: string) {
    return this.m_blocks[root_block_id];
  }

  getLinesGeometry(lines: DbLine[]) {
    let position: number[] = [];
    let index = 0;
    let indexes: number[] = [];
    lines.forEach((item) => {
      item.points.forEach((p) => {
        position.push(p);
      });
      item.indexes?.forEach((v) => {
        indexes.push(v + index);
      });
      index += item.points.length / 3;
    });
    return { index: indexes, position: position };
  }

  addLinesToModel(
    layerId: string,
    color: number,
    lines: DbLine[],
    isCircleCenterAux?: boolean
  ) {
    let material = this.m_context.MaterialManager.createLineMaterial(
      new THREE.Color(color)
    );

    let lineGeo = new THREE.BufferGeometry();
    let posistion: number[] = [];
    let index = 0;
    let indexs: number[] = [];
    let key = `${layerId}_${color}`;

    lines.forEach((item) => {
      item.points.forEach((p, i) => {
        if (i % 3 == 2) {
          if (p > this.m_maxZ) {
            this.m_maxZ = p;
          }
          if (p < this.m_minZ) {
            this.m_minZ = p;
          }
        }
        posistion.push(p);
      });

      item.indexes?.forEach((v) => {
        indexs.push(v + index);
      });
      index += item.points.length / 3;
    });

    let posistionArray = new Float32Array(posistion);
    lineGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(posistionArray, 3)
    );

    lineGeo.setIndex(indexs);
    lineGeo.computeBoundingSphere();
    lineGeo.computeBoundingBox();
    let line: THREE.Line | THREE.LineSegments;
    line = new THREE.LineSegments(lineGeo, material);

    line.renderOrder = -this.m_meshGeoCount;
    line.userData.layerId = layerId;
    line.userData.originalMaterial = line.material;
    if (this.m_layers[layerId]) {
      if (!this.isInsert) {
        this.m_layers[layerId].Objects.push(line);
        this.m_layers[layerId].LineCount =
          1 + (this.m_layers[layerId].LineCount ?? 0);
        if (!this.m_layers[layerId].ObjectColors.includes(color)) {
          this.m_layers[layerId].ObjectColors.push(color);
        }
      }
    }
    if (isCircleCenterAux) {
      if (this.Lines[key]) {
        this.m_model.remove(this.Lines[key].model!);
      }
      this.m_model.add(line);
    } else {
      this.isInsert ? this.m_insertGroup.add(line) : this.m_model.add(line);
    }
    return line;
  }

  OnComplete() {
    for (let key in this.m_blocks) {
      let block = this.m_blocks[key];
      if (block.instances.length <= 0) continue;
      // let lineGeo = new THREE.BufferGeometry();
      // let posistion: number[] = [];
      // let index = 0;
      // let indexs: number[] = [];
      // block.lines?.array.forEach((item) => {
      //   posistion.push(...(item.points || []));
      //   item.indexes?.forEach((v) => {
      //     indexs.push(v + index);
      //   });
      //   index += item.points.length / 3;
      // });
      // lineGeo.setAttribute(
      //   "position",
      //   new THREE.BufferAttribute(new Float32Array(posistion), 3)
      // );
      // lineGeo.setIndex(indexs);
      // let lineMesh = new InstancedMesh(
      //   lineGeo,
      //   undefined,
      //   block.instances.length
      // );
      // index = 0;
      block?.instances?.forEach((instance) => {
        let color = this.m_layers[instance.layerId]?.Color;
        // lineMesh.setColorAt(index, new THREE.Color(color));

        let matrix4 = new THREE.Matrix4();
        instance.matrix.elements.forEach((v, i) => {
          matrix4.elements[i] = v;
        });
        // lineMesh.setMatrixAt(index, matrix4);
        // index++;
        // instance.line = lineMesh;
        // this.m_layers[instance.layerId];
        // this.m_layers[instance.layerId].Objects.push(lineMesh);
      });
      // this.isInsert
      //   ? this.m_insertGroup.add(lineMesh)
      //   : this.m_model.add(lineMesh);
      // posistion.length = 0;
      // block.meshs?.array.forEach((geo) => {
      //   geo.vertexs?.forEach((v) => {
      //     posistion.push(v);
      //   });
      // });
      // let geometry = new THREE.BufferGeometry();
      // geometry.setAttribute(
      //   "position",
      //   new THREE.BufferAttribute(new Float32Array(posistion), 3)
      // );
      // let mesh = new InstancedMesh(geometry, undefined, block.instances.length);
      // index = 0;
      block.instances.forEach((instance) => {
        // mesh.setColorAt(index, new THREE.Color(0xff00ff));
        let matrix4 = new THREE.Matrix4();
        instance.matrix.elements.forEach((v, i) => {
          matrix4.elements[i] = v;
        });
        // mesh.setMatrixAt(index, matrix4);
        // instance.Mesh = mesh;
        // if (this.m_layers[instance.layerId])
        //   this.m_layers[instance.layerId].Objects.push(mesh);

        // index++;
      });
    }
  }

  GetRootReferenceIdDatas(rootId: string | number) {
    let dbentitys: DBEntity[] = [];
    for (let key in this.m_references) {
      let item = this.m_references[key];
      if (key == rootId) {
        dbentitys.push(...item.lines);
        dbentitys.push(...item.mesh);
      }
    }
    return dbentitys;
  }

  /**
   * 返回当前模型内的Block
   */
  public GetBlocks(): DBEntity[] {
    return [];
  }

  /**
   * 根据id获取元素
   * @param id 模型id
   */
  public GetDBEntityById(id: string): DBEntity | undefined {
    for (let key in this.m_lineGeos) {
      let items = this.m_lineGeos[key];
      for (let line of items.lines) {
        if ((line.id = id)) return line;
      }
    }
    for (let key in this.m_meshGeos) {
      let items = this.m_meshGeos[key];
      for (let mesh of items.meshs) {
        if ((mesh.id = id)) return mesh;
      }
    }

    return undefined;
  }

  public isForceCapture: boolean = false;
  /**
   *
   * @param ray 获取相交的元素
   * @param mindis 误差距离
   * @param callback 相交回调
   */
  GetInsertObject(
    ray: THREE.Ray,
    minDis: number,
    callback: (param: IndserObject) => boolean
  ) {
    let param;
    let isContinue = true;
    let isFirst = false;
    for (let key in this.m_lineGeos) {
      let items = this.m_lineGeos[key];
      let layerId = key.split("_")[0];
      if (
        this.LayerTable[layerId].Visible &&
        (this.LayerTable[layerId].Enabled || this.isForceCapture)
      ) {
        let { model } = items;
        let sphere = model.geometry.boundingSphere;
        if (sphere) {
          let res = ray.intersectSphere(sphere, new THREE.Vector3());
          if (!res) continue;
          GetInsertLines(items.lines, ray, minDis, (outParam) => {
            param = outParam;
            param.code = key;
            isContinue = callback(param);
            return isContinue;
          });
        }
        if (param && !isContinue) return;
      }
    }
    for (let key in this.m_meshGeos) {
      let items = this.m_meshGeos[key];
      let layerId = key.split("_")[0];
      if (
        this.LayerTable[layerId].Visible &&
        (this.LayerTable[layerId].Enabled || this.isForceCapture)
      ) {
        let { model } = items;
        let sphere = model.geometry.boundingSphere;
        if (sphere) {
          let res = ray.intersectSphere(sphere, new THREE.Vector3());
          if (!res) continue;
          GetInsertMeshs(items.meshs, ray, minDis, (outParam) => {
            param = outParam;
            param.code = key;
            isContinue = callback(param);
            return isContinue;
          });
        }
        if (!isContinue && param) return;
      }
    }
  }

  public get Instances() {
    return this.m_instances;
  }

  DeleteLayerByIds(...ids: string[]) {
    ids.forEach((id) => {
      this.LayerTable[id].Objects.forEach((obj) => {
        this.m_model.remove(obj);
        FreeThreeObject(obj);
      });
      delete this.LayerTable[id];

      for (let key in this.m_meshGeos) {
        let layerId = key.split("_")[0];
        if (id == layerId) {
          delete this.m_meshGeos[key];
        }
      }

      for (let key in this.m_lineGeos) {
        let layerId = key.split("_")[0];
        if (id == layerId) {
          delete this.m_lineGeos[key];
        }
      }

      for (let key in this.m_blocks) {
        let block = this.m_blocks[key];
        for (let key2 in block.lines) {
          let layerId = key2.split("_")[0];
          if (id == layerId) {
            delete block.lines[key2];
          }
        }
        for (let key2 in block.meshs) {
          let layerId = key2.split("_")[0];
          if (id == layerId) {
            delete block.meshs[key2];
          }
        }
        for (let j = 0; j < block.instances.length; j++) {
          if (block.instances[j].layerId == id) {
            block.instances.splice(j, 1);
          }
        }
      }
    });
    this.m_context.SelectionManager.m_selection.clear();
  }

  DeleteEntity(...entities: DBEntity[]) {
    let deletes: { lines: DbLine[]; mesh: DbMesh[] } = { lines: [], mesh: [] };
    entities.forEach((item) => {
      if (item.root_reference_id) {
        let refs = this.m_references[item.root_reference_id];
        let block = this.m_context.ModelManager.Blocks[item.blockId];
        if (refs) {
          refs.lines.forEach((line) => {
            deletes.lines.push(line);
          });
          refs.mesh.forEach((mesh) => {
            deletes.mesh.push(mesh);
          });
          delete this.m_references[item.root_reference_id];
          if (block) {
            for (let i = 0; i < block.instances.length; i++) {
              const instance = block.instances[i];
              if (instance.id === item.root_reference_id) {
                block.instances.splice(i, 1);
                i--;
              }
            }
          }
        }
        if (this.m_instances.has(item.root_reference_id)) {
          this.m_instances.delete(item.root_reference_id);
        }
      } else {
        if (item instanceof DbLine) {
          deletes.lines.push(item);
        }
        if (item instanceof DbMesh) {
          deletes.mesh.push(item);
        }
      }
    });

    let keys: Set<string> = new Set<string>();
    deletes.lines.forEach((item) => {
      let lineGeo, color, keyValue;
      for (let key in this.m_lineGeos) {
        let lineGeo = this.m_lineGeos[key];
        let layerId = key.split("_")[0];
        if (
          lineGeo.canEdit &&
          item.layerId == layerId &&
          this.m_lineGeos[key].lines.indexOf(item) > -1
        ) {
          lineGeo.lines.splice(this.m_lineGeos[key].lines.indexOf(item), 1);
          color = key.split("_")[1];
          keyValue = key;
          keys.add(keyValue);
        }
      }
    });
    keys.forEach((key) => {
      let value = this.m_lineGeos[key];
      let lines = this.m_lineGeos[key].lines;
      let model = this.m_lineGeos[key].model;
      if (lines.length == 0) {
        let layer = this.LayerTable[key.split("_")[0]];
        if (layer) {
          layer.LineCount = Number(layer.LineCount) - 1;
          layer.Objects.forEach((obj, index) => {
            if (obj.uuid == model.uuid) {
              layer.Objects.splice(index, 1);
            }
          });
          // let index = layer.Objects.indexOf(model);
          // if (index > 0) {
          //   layer.Objects.splice(index, 1);
          // }
        }
        let { color } = model.material as THREE.LineBasicMaterial;
        let colorHex = color.getHex();
        if (layer.ObjectColors.includes(colorHex)) {
          layer.ObjectColors.splice(layer.ObjectColors.indexOf(colorHex), 1);
        }
        this.m_model.remove(model);
        model.geometry.dispose();
        delete this.m_lineGeos[key];
      } else {
        let geo = this.getLinesGeometry(lines);
        model.geometry.setAttribute(
          "position",
          new THREE.BufferAttribute(new Float32Array(geo.position), 3)
        );
        if (model.material instanceof THREE.LineDashedMaterial) {
          model.computeLineDistances();
        } else {
          model.geometry.setIndex(geo.index);
        }
      }
    });
    keys.clear();
    deletes.mesh.forEach((item) => {
      let meshGeo, color, keyValue;
      for (let key in this.m_meshGeos) {
        let layerId = key.split("_")[0];
        meshGeo = this.m_meshGeos[key];
        if (
          meshGeo.canEdit &&
          item.layerId == layerId &&
          meshGeo.meshs.indexOf(item) > -1
        ) {
          meshGeo.meshs.splice(this.m_meshGeos[key].meshs.indexOf(item), 1);
          color = key.split("_")[1];
          keyValue = key;
          keys.add(keyValue);
        }
      }
    });
    keys.forEach((key) => {
      let value = this.m_meshGeos[key];
      if (!value.canEdit) return;
      let mesh = this.m_meshGeos[key].meshs;
      let model = this.m_meshGeos[key].model;
      if (mesh.length == 0) {
        let layer = this.LayerTable[key.split("_")[0]];
        if (layer) {
          layer.MeshCount = Number(layer.MeshCount) - 1;
          let index = layer.Objects.indexOf(model);
          if (index > 0) {
            layer.Objects.splice(index, 1);
          }
        }
        let { color } = model.material as THREE.MeshBasicMaterial;
        let colorHex = color.getHex();
        if (layer.ObjectColors.includes(colorHex)) {
          layer.ObjectColors.splice(layer.ObjectColors.indexOf(colorHex), 1);
        }
        this.m_model.remove(model);
        model.geometry.dispose();
        delete this.m_meshGeos[key];
      } else {
        let position: number[] = [];
        mesh.forEach((item) => {
          item.vertexs?.forEach((p) => {
            position.push(p);
          });
        });
        model.geometry.setAttribute(
          "position",
          new THREE.BufferAttribute(new Float32Array(position), 3)
        );
      }
    });
    this.m_context.ViewManager.Render();
  }

  // DelInstances(instances: DbBlockInstance[]) {
  //   let rootIds: string[] = [];
  //   instances.forEach((instance) => {
  //     rootIds.push(instance.id!);
  //   });
  //   rootIds = Array.from(new Set(rootIds));
  //   for (const key in this.m_references) {
  //     if (rootIds.includes(key)) {
  //       let refs = this.m_references[key];
  //       if (refs) {
  //         this.DeleteEntity(...refs.lines);
  //         this.DeleteEntity(...refs.mesh);
  //       }
  //     }
  //   }
  // }

  // 计算数组中元素的个数
  computeArrItemCount(arr: any[]) {
    return arr.reduce((obj, cur) => {
      if (cur in obj) {
        obj[cur]++;
      } else {
        obj[cur] = 1;
      }
      return obj;
    }, {});
  }
  private layerNameCounts: { [key: string]: number } = {};
  /**
   *
   * @param layerName 图层名称
   * @param points 点集
   * @param lineId 线id
   */
  AddCustomLines(
    layerName: string,
    color: number,
    points: THREE.Vector3[],
    isContinuous: boolean = true,
    extendId: string = ""
  ) {
    let layer = this.GetLayerByNameColor(layerName, color);
    
    let addLines: DbLine[] = [];
    if (layer) {
      let key = `${layer.Id}_${color}`;
      let lines: DbLine[] = this.m_lineGeos[key]?.lines ?? [];
      let posistion: number[] = [];
      let index = 0;
      let indexs: number[] = [];
      points.forEach((p) => {
        posistion.push(p.x, p.y, p.z);
        indexs.push(index);
        if (isContinuous && index != 0 && index != points.length - 1) {
          indexs.push(index);
        }
        index++;
      });
      let line = new DbLine({
        id: generateUUID(),
        layerId: layer.Id,
        blockId: "",
        points: new Float32Array(posistion),
        indexes: new Int32Array(indexs),
        color: color,
        extend_id: extendId ?? generateUUID(),
      });
      line.getBoundingBox();
      line.getBoundingSphere();
      lines.push(line);
      addLines.push(line);
      this.createDbLines.push(line);
      if (this.m_lineGeos[key]) {
        this.m_lineGeos[key].lines = lines;
        this.m_context.ToolsManager.LinesToModel(
          lines,
          this.m_lineGeos[key].model
        );
      } else {
        this.m_lineGeos[key] = {
          lines: lines,
          model: this.addLinesToModel(layer.Id, color, lines),
          canEdit: true,
        };
      }
      return addLines;
    } else {
      throw new Error("没有找到layName");
    }
  }

  /**
   *
   * @param layerName 图层名称
   * @param color
   * @param vertexs 点集
   */
  AddCustomMeshs(layerName: string, color: number, vertexs: number[]) {
    let layer = this.GetLayerByNameColor(layerName, color);
    let addMeshs: DbMesh[] = [];
    if (layer) {
      let key = layerName + "_" + color;
      let meshs = this.Meshs[key];
      let mesh = new DbMesh({
        id: generateUUID(),
        layerId: layer.Id,
        blockId: "",
        vertexs: new Float32Array(vertexs),
        indexes: new Int32Array([]),
        color: layer.Color,
        extend_id: generateUUID(),
      });
      mesh.getBoundingBox();
      mesh.getBoundingSphere();
      meshs.meshs.push(mesh);
      addMeshs.push(mesh);
      this.m_context.ToolsManager.MeshToModel(meshs.meshs, meshs.model);
      return addMeshs;
    } else {
      throw new Error("没有找到layName");
    }
  }

  private createDbLines: DbLine[] = [];
  // /**
  //  * @param layerName 图层名称
  //  * @param points 点集
  //  */
  // CreateCircle(
  //   layerName: string,
  //   circleData: {
  //     radius: number;
  //     center: { x: number; y: number; z: number };
  //   }[],
  //   callback: (createDbLines: DbLine[]) => void
  // ) {
  //   // let list = circleData.list;
  //   // let radius = circleData.radius;
  //   this.createDbLines.length = 0;
  //   for (let i = 0; i < circleData.length; i++) {
  //     let circlePoints: THREE.Vector3[] = [];
  //     for (let j = 0; j <= 36; j++) {
  //       let points = new THREE.Vector3(
  //         circleData[i].center.x +
  //           circleData[i].radius * Math.cos(((Math.PI * 2) / 36) * j),
  //         circleData[i].center.y +
  //           circleData[i].radius * Math.sin(((Math.PI * 2) / 36) * j),
  //         0
  //       );
  //       circlePoints.push(points);
  //     }
  //     this.AddCustomLines(layerName, circlePoints);

  //     let count = 0;
  //     this.Layers.forEach((layer: DbLayer) => {
  //       if (layer.Name === layerName && count < 1) {
  //         if (this.layerNameCounts[layerName] > 1) {
  //           count++;
  //         }
  //         let key = `${layer.Id}_${layer.Color}`;
  //         let lines: DbLine[] = this.m_lineGeos[key]?.lines ?? [];
  //         //创建圆所添加的DbLine的block_path要保持唯一性
  //         lines[lines.length - 1].block_path = generateUUID();
  //         //创建圆的buffGeometry要唯一，否则会出现多个圆，最后一个会合并之前的，所以要删除之前的
  //         if (i !== circleData.length - 1) {
  //           let lastObj = this.m_layers[layer.Id].Objects.pop();
  //           this.m_model.remove(lastObj!);
  //         }
  //       }
  //     });
  //   }
  //   callback(this.createDbLines);
  // }

  /**
   * @param layerName 图层名称
   * @param points 点集
   */
  // CreateRectangle(
  //   layerName: string,
  //   rectangleData: {
  //     width: number;
  //     height: number;
  //     center: THREE.Vector3;
  //   }[],
  //   angle: number,
  //   callback: (createDbLines: DbLine[]) => void
  // ) {
  //   this.createDbLines.length = 0;
  //   for (let i = 0; i < rectangleData.length; i++) {
  //     let rectanglePoints: THREE.Vector3[] = [];

  //     rectanglePoints.push(
  //       new THREE.Vector3(
  //         rectangleData[i].center.x - rectangleData[i].width / 2,
  //         rectangleData[i].center.y - rectangleData[i].height / 2,
  //         0
  //       ),
  //       new THREE.Vector3(
  //         rectangleData[i].center.x + rectangleData[i].width / 2,
  //         rectangleData[i].center.y - rectangleData[i].height / 2,
  //         0
  //       ),
  //       new THREE.Vector3(
  //         rectangleData[i].center.x + rectangleData[i].width / 2,
  //         rectangleData[i].center.y + rectangleData[i].height / 2,
  //         0
  //       ),
  //       new THREE.Vector3(
  //         rectangleData[i].center.x - rectangleData[i].width / 2,
  //         rectangleData[i].center.y + rectangleData[i].height / 2,
  //         0
  //       ),
  //       new THREE.Vector3(
  //         rectangleData[i].center.x - rectangleData[i].width / 2,
  //         rectangleData[i].center.y - rectangleData[i].height / 2,
  //         0
  //       )
  //     );
  //     rectanglePoints.forEach((item) => {
  //       item.sub(rectangleData[i].center);
  //       item.applyAxisAngle(new THREE.Vector3(0, 0, 1), angle);
  //       item.add(rectangleData[i].center);
  //     });
  //     this.AddCustomLines(layerName, rectanglePoints);

  //     let count = 0;
  //     this.Layers.forEach((layer: DbLayer) => {
  //       if (layer.Name === layerName && count < 1) {
  //         if (this.layerNameCounts[layerName] > 1) {
  //           count++;
  //         }
  //         let key = `${layer.Id}_${layer.Color}`;
  //         let lines: DbLine[] = this.m_lineGeos[key]?.lines ?? [];
  //         //创建圆所添加的DbLine的block_path要保持唯一性
  //         lines[lines.length - 1].block_path = generateUUID();
  //         //创建圆的buffGeometry要唯一，否则会出现多个圆，最后一个会合并之前的，所以要删除之前的
  //         if (i !== rectangleData.length - 1) {
  //           let lastObj = this.m_layers[layer.Id].Objects.pop();
  //           this.m_model.remove(lastObj!);
  //         }
  //       }
  //     });
  //   }
  //   callback(this.createDbLines);
  // }

  public get Box() {
    let box = new THREE.Box3();
    box.setFromObject(this.m_model);
    return box;
  }
  public Clear() {
    this.m_layers = {};
    this.m_lineGeos = {};
    this.m_meshGeos = {};
    this.m_instances = new Map();
    this.m_blocks = {};
    this.m_lineGeoCount = 0;
    this.m_meshGeoCount = 0;
    this.m_model.clear();
    this.m_context.SceneManager.MaskLayer.clear();
    // this.m_context.SceneManager.ModelClear();
    this.m_context.ViewManager.Render();
  }

  // 创建临时数据
  CreateTempModel(params: {
    positions: number[][];
    color: number;
    type: number;
    opacity: number;
  }) {
    let { positions, color, type, opacity } = params;
    let geo = new THREE.BufferGeometry();
    geo.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(positions.flat(), 3)
    );
    let idx = 0;
    let indexs: number[] = [];
    if (type == 0) {
      for (let i = 0; i < positions.length; i++) {
        let ps = positions[i];
        let length = ps.length / 3;
        for (let i = 0; i < length; i++) {
          indexs.push(idx + i);
          if (i != 0 && i != length - 1) {
            indexs.push(idx + i);
          }
        }
        idx += length;
      }
      // 线模型
      let material = new THREE.LineBasicMaterial({
        color: color,
        opacity: opacity,
        transparent: true,
      });
      geo.setIndex(indexs);
      let model = new THREE.LineSegments(geo, material);
      model.name = "tempModel";
      this.m_context.SceneManager.AuxModel.add(model);
    } else if (type == 1) {
      // mesh模型
      let material = new THREE.MeshStandardMaterial({
        color: color,
        opacity: opacity,
        transparent: true,
      });
      let model = new THREE.Mesh(geo, material);
      model.name = "tempModel";
      this.m_context.SceneManager.AuxModel.add(model);
    }
    this.m_context.ViewManager.Render();
  }

  // 清空临时模型
  ClearTempModel() {
    let tempModels: THREE.Object3D[] = [];
    this.m_context.SceneManager.AuxModel.children.forEach((item) => {
      if (item.name == "tempModel") {
        tempModels.push(item);
      }
    });
    this.m_context.ToolsManager.ObjectsClear(tempModels);
    this.m_context.ViewManager.Render();
  }

  public ByDataUpdateModel(data: DbLine | DbMesh) {
    data.getBoundingBox();
    data.getBoundingSphere();
    let rootId = data.root_reference_id;
    let key = data.layerId + "_" + data.color;
    let lines = this.Lines[key] ? this.Lines[key].lines : null;
    let meshs = this.Meshs[key] ? this.Meshs[key].meshs : null;
    let lineModel = this.Lines[key] ? this.Lines[key].model : null;
    let meshModel = this.Meshs[key] ? this.Meshs[key].model : null;
    if (rootId) {
      let line = this.Group[rootId]?.lines[0];
      let mesh = this.Group[rootId]?.mesh[0];
      let lineKey = line?.layerId + "_" + line?.color;
      let meshKey = mesh?.layerId + "_" + mesh?.color;
      lineModel = this.Lines[lineKey]?.model;
      meshModel = this.Meshs[meshKey]?.model;
      lines = this.Lines[lineKey]?.lines;
      meshs = this.Meshs[meshKey]?.meshs;
    }
    if (lineModel && lines) {
      let lineGeoPositions = [];
      for (let i = 0; i < lines.length; i++) {
        lineGeoPositions.push(...lines[i].points);
      }
      lineModel.geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(lineGeoPositions, 3)
      );
      lineModel.geometry.computeBoundingBox();
      lineModel.geometry.computeBoundingSphere();
    }
    if (meshModel && meshs) {
      let meshGeoPositions = [];
      for (let i = 0; i < meshs.length; i++) {
        meshGeoPositions.push(...meshs[i].vertexs);
      }
      meshModel.geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(meshGeoPositions, 3)
      );
      meshModel.geometry.computeBoundingBox();
      meshModel.geometry.computeBoundingSphere();
    }
    this.m_context.ViewManager.Render();
  }

  public ByDatasUpdateModel(datas: (DbLine | DbMesh)[]) {
    for (let i = 0; i < datas.length; i++) {
      let data = datas[i];
      data.getBoundingBox();
      data.getBoundingSphere();
      let key = this.m_context.ToolsManager.getKey(data);
      if (data instanceof DbLine) {
        if (this.Lines[key]) {
          let { lines } = this.Lines[key];
          for (let j = 0; j < lines.length; j++) {
            let line = lines[j];
            let str = line.block_path + line.id + line.extend_id;
            let dataStr = data.block_path + data.id + data.extend_id;
            if (str == dataStr) {
              lines.splice(j, 1, data);
              break;
            }
          }
        }
        if (data.root_reference_id) {
          let { lines, mesh } = this.Group[data.root_reference_id];
          for (let j = 0; j < lines.length; j++) {
            let line = lines[j];
            let str = line.block_path + line.id + line.extend_id;
            let dataStr = data.block_path + data.id + data.extend_id;
            if (str == dataStr) {
              lines.splice(j, 1, data);
              break;
            }
          }
          if (data?.userData?.originMatrix) {
            let instanceData = this.m_context.ModelManager.Instances.get(
              data.root_reference_id
            );
            instanceData?.matrix.copy(data.userData.originMatrix);
            delete data.userData.originMatrix;
          }
        }
      } else if (data instanceof DbMesh) {
        if (this.Meshs[key]) {
          let { meshs } = this.Meshs[key];
          for (let j = 0; j < meshs.length; j++) {
            let line = meshs[j];
            let str = line.block_path + line.id + line.extend_id;
            let dataStr = data.block_path + data.id + data.extend_id;
            if (str == dataStr) {
              meshs.splice(j, 1, data);
              break;
            }
          }
        }
        if (data.root_reference_id) {
          let { mesh } = this.Group[data.root_reference_id];
          for (let j = 0; j < mesh.length; j++) {
            let line = mesh[j];
            let str = line.block_path + line.id + line.extend_id;
            let dataStr = data.block_path + data.id + data.extend_id;
            if (str == dataStr) {
              mesh.splice(j, 1, data);
              break;
            }
          }
          if (data?.userData?.originMatrix) {
            let instanceData = this.m_context.ModelManager.Instances.get(
              data.root_reference_id
            );
            instanceData?.matrix.copy(data.userData.originMatrix);
            delete data.userData.originMatrix;
          }
        }
      }
    }
    for (let i = 0; i < datas.length; i++) {
      let data = datas[i];
      if (data instanceof DbLine) {
        let key = this.m_context.ToolsManager.getKey(data);
        if (this.Lines[key]) {
          this.m_context.ToolsManager.LinesToModel(
            this.Lines[key].lines,
            this.Lines[key].model
          );
        }
      } else if (data instanceof DbMesh) {
        let key = this.m_context.ToolsManager.getKey(data);
        if (this.Meshs[key]) {
          this.m_context.ToolsManager.MeshToModel(
            this.Meshs[key].meshs,
            this.Meshs[key].model
          );
        }
      }
    }

    this.m_context.ViewManager.Render();
  }

  private m_changeColorGroup = new THREE.Group();
  public get ChangeColorGroup() {
    return this.m_changeColorGroup;
  }
  // 更改对象为指定颜色
  // public ChangeColor(datas: (DbLine | DbMesh)[], layer: DbLayer) {
  //   let lines: DbLine[] = [];
  //   let meshs: DbMesh[] = [];
  //   for (let i = 0; i < datas.length; i++) {
  //     let data = datas[i];
  //     if (data instanceof DbLine) {
  //       let copyData = DbLine.clone(data);
  //       copyData.color = layer.Color;
  //       copyData.layerId = layer.Id;
  //       lines.push(copyData);
  //     } else if (data instanceof DbMesh) {
  //       let copyData = DbMesh.clone(data);
  //       copyData.color = layer.Color;
  //       copyData.layerId = layer.Id;
  //       meshs.push(copyData);
  //     }
  //   }
  //   let key = layer.Id + "_" + layer.Color;

  //   if (this.m_context.ModelManager.Lines[key]) {
  //     this.m_context.ModelManager.Lines[key].lines.push(...lines);
  //     this.m_context.ToolsManager.LinesToModel(
  //       this.m_context.ModelManager.Lines[key].lines,
  //       this.m_context.ModelManager.Lines[key].model
  //     );
  //   } else {
  //     this.m_context.ModelManager.Lines[key] = {
  //       canEdit: true,
  //       lines: lines,
  //       model: this.m_context.ModelManager.addLinesToModel(
  //         layer.Id,
  //         layer.Color!,
  //         lines
  //       ),
  //     };
  //   }
  // }
  public ChangeColor(datas: (DbLine | DbMesh)[], layer: DbLayer) {
    let isAddGroup = true;
    this.m_context.SceneManager.AuxModel.children.forEach((auxItem) => {
      if (auxItem.name == "changeColorGroup") {
        isAddGroup = false;
      }
    });
    if (isAddGroup) {
      this.m_changeColorGroup = new THREE.Group();
      this.m_changeColorGroup.name = "changeColorGroup";
      this.m_context.SceneManager.AuxModel.add(this.m_changeColorGroup);
    }

    for (let i = 0; i < datas.length; i++) {
      let data = datas[i];
      if (data instanceof DbLine) {
        let lineGeo = new THREE.BufferGeometry();
        let material = new THREE.LineBasicMaterial({
          color: new THREE.Color(layer.Color),
          side: THREE.DoubleSide,
        });
        if (data.userData?.model) {
          lineGeo = data.userData.model.geometry;
          material = data.userData.model.material;
          material.color.set(new THREE.Color(layer.Color));
        }
        lineGeo.setAttribute(
          "position",
          new THREE.BufferAttribute(new Float32Array(data.points), 3)
        );
        let index = data.indexes ? [...data.indexes] : [];
        lineGeo.setIndex(index);
        if (!data.userData?.model) {
          let line = new THREE.LineSegments(lineGeo, material);
          line.renderOrder = 1;
          this.m_changeColorGroup.add(line);
          data.userData = {
            model: line,
            layerId: layer.Id,
            data,
          };
        } else {
          data.userData.layerId = layer.Id;
          data.userData.data = data;
        }
      } else if (data instanceof DbMesh) {
        let meshGeo = new THREE.BufferGeometry();
        let material = new THREE.MeshBasicMaterial({
          color: new THREE.Color(layer.Color),
          side: THREE.DoubleSide,
        });
        if (data.userData?.model) {
          meshGeo = data.userData.model.geometry;
          material = data.userData.model.material;
          material.color.set(new THREE.Color(layer.Color));
        }
        meshGeo.setAttribute(
          "position",
          new THREE.BufferAttribute(new Float32Array(data.vertexs), 3)
        );

        if (!data.userData?.model) {
          let mesh = new THREE.Mesh(meshGeo, material);
          mesh.renderOrder = 1;
          this.m_changeColorGroup.add(mesh);
          data.userData = {
            model: mesh,
            layerId: layer.Id,
            data,
          };
        } else {
          data.userData.layerId = layer.Id;
          data.userData.data = data;
        }
      }
    }
  }

  // 删除指定修改颜色的对象
  public DelChangeColor(datas: (DbLine | DbMesh)[]) {
    if (
      this.m_changeColorGroup &&
      this.m_changeColorGroup.children.length &&
      datas.length
    ) {
      for (let i = 0; i < datas.length; i++) {
        let data = datas[i];
        for (let i = 0; i < this.m_changeColorGroup.children.length; i++) {
          let child = this.m_changeColorGroup.children[i];
          if (child.userData.uid == data.id + data.block_path) {
            if (
              child instanceof THREE.LineSegments ||
              child instanceof THREE.Mesh
            ) {
              child.geometry.dispose();
              child.material.dispose();
              child.removeFromParent();
            }
          }
        }
      }
    }
  }

  // 清空修改颜色对象组
  public ClearChangeColor() {
    if (this.m_changeColorGroup && this.m_changeColorGroup.children.length) {
      this.m_context.ToolsManager.GroupClear(this.m_changeColorGroup);
      // for (let i = 0; i < this.m_changeColorGroup.children.length; i++) {
      //   this.m_changeColorGroup.children[i].removeFromParent();
      // }
      // this.m_changeColorGroup.removeFromParent();
    }
  }

  public PassLayerNameGetData(layerNames: string[]) {
    let selectAll: (DbLine | DbMesh)[] = [];
    let { Lines, Meshs } = this.m_context.ModelManager;
    for (let i = 0; i < layerNames.length; i++) {
      let layer = this.m_context.ModelManager.GetLayerByName(layerNames[i]);
      if (layer) {
        for (const key in Lines) {
          let layerId = key.split("_")[0];
          if (layerId == layer.Id) {
            let { lines } = Lines[key];
            selectAll.push(...lines);
          }
        }
        for (const key in Meshs) {
          let layerId = key.split("_")[0];
          if (layerId == layer.Id) {
            let { meshs } = Meshs[key];
            selectAll.push(...meshs);
          }
        }
      }
    }

    return selectAll;
  }

  // 通过entityIdS得到原始图纸数据;
  public PassEntityIdsGetData(entityIds: string[]) {
    let getDatas: {
      lines: DbLine[];
      meshs: DbMesh[];
    } = {
      lines: [],
      meshs: [],
    };
    if (entityIds?.length) {
      for (const key in this.Lines) {
        let { lines } = this.Lines[key];
        for (let i = 0; i < lines.length; i++) {
          let line = lines[i];
          if (line?.entity_id && entityIds.indexOf(line?.entity_id) !== -1) {
            getDatas.lines.push(line);
          }
        }
      }
      for (const key in this.Meshs) {
        let { meshs } = this.Meshs[key];
        for (let i = 0; i < meshs.length; i++) {
          let mesh = meshs[i];
          if (mesh?.entity_id && entityIds.indexOf(mesh?.entity_id) !== -1) {
            getDatas.meshs.push(mesh);
          }
        }
      }
    }

    return getDatas;
  }

  // 通过sourceIds得到原始图纸数据;
  public PassSourceIdsGetData(sourceIds: string[]) {
    let getDatas: {
      lines: DbLine[];
      meshs: DbMesh[];
    } = {
      lines: [],
      meshs: [],
    };
    if (sourceIds?.length) {
      for (const key in this.Lines) {
        let { lines } = this.Lines[key];
        for (let i = 0; i < lines.length; i++) {
          let line = lines[i];
          if (line?.source_id && sourceIds.indexOf(line?.source_id) !== -1) {
            getDatas.lines.push(line);
          }
        }
      }
      for (const key in this.Meshs) {
        let { meshs } = this.Meshs[key];
        for (let i = 0; i < meshs.length; i++) {
          let mesh = meshs[i];
          if (mesh?.source_id && sourceIds.indexOf(mesh?.source_id) !== -1) {
            getDatas.meshs.push(mesh);
          }
        }
      }
    }

    return getDatas;
  }
}
